import React, { useState, useRef, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { topics, BlogPost } from "../../data/blog/blog";
import { useBlogPosts } from "../../hooks/useBlogPosts";
import SmartPagination from "./SmartPagination";

function shuffle(array: BlogPost[]) {
  return array
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
}

const POSTS_PER_PAGE = 9;

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { posts: allPosts, loading } = useBlogPosts();
  const [selectedTopic, setSelectedTopic] =
    useState("All Topics");
  const [page, setPage] = useState(() => {
    // Khôi phục page từ sessionStorage khi component mount
    const savedPage = sessionStorage.getItem("blogPage");
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const shuffledPosts = useMemo(() => shuffle(allPosts), [allPosts]);
  const topicBarRef = useRef<HTMLDivElement | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");

  // Lưu page vào sessionStorage mỗi khi thay đổi
  useEffect(() => {
    sessionStorage.setItem("blogPage", page.toString());
  }, [page]);

  // Reset page khi rời khỏi /blog
  useEffect(() => {
    return () => {
      if (location.pathname !== "/blog") {
        sessionStorage.removeItem("blogPage");
      }
    };
  }, [location.pathname]);

  // Xử lý topic/keyword từ query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const topicParam = params.get("topic");
    const keywordParam = params.get("keyword");
    if (keywordParam) {
      setSelectedTopic("All Topics");
      setPage(1); // Reset page khi có keyword
      sessionStorage.setItem("blogPage", "1");
      setSearchKeyword(keywordParam);
    } else if (topicParam && topicParam !== selectedTopic) {
      setSelectedTopic(topicParam);
      setPage(1); // Reset page khi thay đổi topic
      sessionStorage.setItem("blogPage", "1");
      setSearchKeyword("");
    } else if (!topicParam && !keywordParam) {
      setSearchKeyword("");
    }
  }, [location.search, selectedTopic]);

  // Filter and paginate posts
  const filteredPosts = searchKeyword
    ? shuffledPosts.filter((post) =>
        post.keyword
          .toLowerCase()
          .includes(searchKeyword.toLowerCase())
      )
    : selectedTopic === "All Topics"
    ? shuffledPosts
    : shuffledPosts.filter(
        (post) => post.topic === selectedTopic
      );
  const totalPages =
    Math.ceil(filteredPosts.length / POSTS_PER_PAGE) || 1;
  const paginatedPosts = filteredPosts.slice(
    (page - 1) * POSTS_PER_PAGE,
    page * POSTS_PER_PAGE
  );

  // Scroll to topic bar with header offset
  const handleSelectTopic = (topic: string) => {
    setSelectedTopic(topic);
    setTimeout(() => {
      const header = document.querySelector(".header");
      const headerHeight = header
        ? (header as HTMLElement).offsetHeight
        : 0;
      if (topicBarRef.current) {
        const rect =
          topicBarRef.current.getBoundingClientRect();
        const scrollTop =
          window.scrollY + rect.top - headerHeight - 8;
        window.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    }, 50);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setTimeout(() => {
      const header = document.querySelector(".header");
      const headerHeight = header
        ? (header as HTMLElement).offsetHeight
        : 0;
      if (topicBarRef.current) {
        const rect =
          topicBarRef.current.getBoundingClientRect();
        const scrollTop =
          window.scrollY + rect.top - headerHeight - 8;
        window.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    }, 50);
  };

  const handlePostClick = (post: BlogPost) => {
    const titleSlug = post.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
    navigate(`/blog/${titleSlug}`);
  };

  return (
    <>
      <Helmet>
        <title>Blog - VEA Group Insights & Updates</title>
        <meta
          name="description"
          content="Explore the latest insights, updates, and articles from VEA Group about e-commerce, digital marketing, and business growth strategies."
        />
        <meta
          name="keywords"
          content="blog, e-commerce, digital marketing, business growth, VEA Group, insights, updates"
        />
        <link
          rel="canonical"
          href="https://veagroup.global/blog"
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="manifest" href="/manifest.json" />
        {/* Open Graph */}
        <meta
          property="og:title"
          content="Blog - VEA Group Insights & Updates"
        />
        <meta
          property="og:description"
          content="Explore the latest insights, updates, and articles from VEA Group about e-commerce, digital marketing, and business growth strategies."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://veagroup.global/blog"
        />
        <meta property="og:image" content="/logo.png" />
        {/* Twitter Card */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content="Blog - VEA Group Insights & Updates"
        />
        <meta
          name="twitter:description"
          content="Explore the latest insights, updates, and articles from VEA Group about e-commerce, digital marketing, and business growth strategies."
        />
        <meta name="twitter:image" content="/logo.png" />
      </Helmet>
      {/* Header with Background Image */}
      <header
        className="header relative w-full bg-cover bg-center text-center"
        style={{
          backgroundImage:
            "url('/images/blog_background.jpg')",
          minHeight: "400px",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
          <h1 className="text-5xl font-bold text-white">
            Blog
          </h1>
        </div>
      </header>
      <div className="bg-[#fafafa] dark:bg-black min-h-screen py-16 px-4 md:px-8 transition-colors duration-300">
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
        <>
        <div className="container mx-auto px-4 py-4">
          {/* Featured Posts */}
          <div className="w-full flex items-center justify-center py-8 transition-colors duration-500">
            <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-8" />
            <span className="text-5xl font-bold bg-gradient-to-r from-blue-700 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_2px_16px_rgba(30,64,175,0.18)] whitespace-nowrap">
              <span className="bg-gradient-to-r from-[#FE6019] to-[#ffb347] bg-clip-text text-transparent drop-shadow-[0_2px_16px_rgba(254,96,25,0.25)]">
                FEATURES
              </span>{" "}
              POSTS
            </span>
            <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-8" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {allPosts
              .filter((p) => p.featured)
              .slice(0, 4)
              .map((post) => (
                <div
                  key={post.id}
                  className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 flex flex-col md:flex-row gap-6 transition-colors duration-300 backdrop-blur-sm hover:backdrop-blur-md cursor-pointer"
                  onClick={() => handlePostClick(post)}
                >
                  <div className="relative w-full md:w-1/3">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 md:w-[235px] md:h-[220px] object-cover rounded-lg shadow-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-20 rounded-lg"></div>
                  </div>
                  <div className="flex-1 flex-col justify-between space-y-6">
                    <button
                      className="inline-block h-8 rounded-3xl border border-primary bg-[#FAEBE1] px-3.5 py-2 text-sm leading-none text-primary transition-all hover:bg-primary hover:text-white cursor-pointer relative left-0 top-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectTopic(post.topic);
                      }}
                    >
                      {post.topic}
                    </button>
                    <h3 className="font-bold text-xl text-black dark:text-white transition-colors duration-300">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      {post.desc}
                    </p>
                    <div className="flex items-center text-xs text-gray-400 dark:text-gray-500 space-x-4">
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 inline-block mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {post.date}
                      </span>
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 inline-block mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {post.time}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
        {/* Topic Bar */}
        <div
          ref={topicBarRef}
          className="flex flex-wrap gap-4 bg-gray-100 dark:bg-gray-800 rounded-xl p-4 mb-8 transition-colors duration-300"
        >
          {topics.map((topic) => (
            <button
              key={topic}
              className={`px-5 py-2 rounded-full border text-sm font-medium transition-all duration-200 ${
                selectedTopic === topic
                  ? "bg-gradient-to-br from-primary to-secondary text-white border-none shadow-md"
                  : "bg-white dark:bg-orange-500 dark:bg-opacity-10 dark:border-orange-500 text-black dark:text-white border-gray-300 hover:bg-gray-200 dark:hover:bg-opacity-20"
              }`}
              onClick={() => handleSelectTopic(topic)}
            >
              {topic}
            </button>
          ))}
        </div>
        {/* Posts by Topic */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {paginatedPosts.map((post) => (
            <div
              key={post.id}
              className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-4 flex flex-col transition-colors duration-300 backdrop-blur-sm hover:backdrop-blur-md space-y-6 cursor-pointer"
              onClick={() => handlePostClick(post)}
            >
              <div className="relative">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 md:w-[335px] md:h-[210px] object-cover rounded-lg shadow-lg"
                />
                <div className="absolute inset-0 bg-black bg-opacity-20 rounded-lg"></div>
              </div>
              <button
                className="inline-block h-8 rounded-3xl border border-primary bg-[#FAEBE1] px-3.5 py-2 text-sm leading-none text-primary transition-all hover:bg-primary hover:text-white cursor-pointer absolute right-10 top-5 z-[1] mb-6"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectTopic(post.topic);
                }}
              >
                {post.topic}
              </button>
              <h3 className="font-bold text-lg mb-1 text-black dark:text-white transition-colors duration-300">
                {post.title}
              </h3>
              <div className="flex flex-col items-start justify-center flex-1">
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                  {post.desc}
                </p>
                <div className="flex items-center text-xs text-gray-400 dark:text-gray-500 space-x-4 mt-auto">
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 inline-block mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {post.date}
                  </span>
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 inline-block mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {post.time}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Pagination Bar */}
        <SmartPagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
        </>
        )}
      </div>
    </>
  );
};

export default Blog;
